import { ApolloError } from "@apollo/client";
import { Alert, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { SelectElement, SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlProjectStatus } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import { parseDateToString, parseStringToDate } from "~/modules/app/util/dateHelpers";

type Props = {
    initialData: ProjectFormData;
    onSubmit: SubmitHandler<ProjectFormData>;
    formButtonLabel: string;
    error?: ApolloError;
    isStatusChangeAllowed?: boolean;
    statusChangeReason?: string | null | undefined;
};

interface ProjectFormValues extends Omit<ProjectFormData, "startAt" | "status"> {
    startAt?: Date;
    status?: GqlProjectStatus;
}

export default function ProjectForm({
    initialData,
    onSubmit,
    error,
    formButtonLabel,
    isStatusChangeAllowed = true,
    statusChangeReason,
}: Props) {
    const { t } = useTranslation("project");

    const {
        handleSubmit,
        register,
        setError,
        formState: { errors, isSubmitting, isLoading, isDirty, isSubmitted },
        control,
    } = useForm<ProjectFormValues>({
        values: {
            ...initialData,
            startAt: initialData?.startAt ? parseStringToDate(initialData.startAt) : undefined,
        },
    });

    const handleOnSubmit = (values: ProjectFormValues) => {
        onSubmit({
            ...values,
            // The status select is disabled when status changes aren't allowed, which causes
            // react-hook-form to omit the field from the submitted values. Fall back to the
            // current status so the always-required input is still provided.
            status: values.status ?? initialData.status,
            startAt: values.startAt ? parseDateToString(values.startAt) : undefined,
        });
    };

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);
    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(handleOnSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}
                <TextField
                    label={t("projectForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    {...register("name")}
                />

                <Controller
                    control={control}
                    name="startAt"
                    render={({ field }) => (
                        <DatePicker
                            label={t("projectForm.labelStartDate")}
                            value={field.value || null}
                            onChange={date => field.onChange(date)}
                            slotProps={{
                                textField: {
                                    error: !!errors.startAt,
                                },
                            }}
                        />
                    )}
                />
                <Divider>
                    <Typography variant="caption" textTransform="uppercase">
                        {t("projectForm.sectionContact")}
                    </Typography>
                </Divider>
                <TextField
                    label={t("projectForm.labelContactName")}
                    error={!!errors.contactName}
                    helperText={errors.contactName?.message}
                    {...register("contactName")}
                />
                <TextField
                    label={t("projectForm.labelContactEmail")}
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail?.message}
                    {...register("contactEmail")}
                />
                <TextField
                    label={t("projectForm.labelContactPhone")}
                    error={!!errors.contactPhone}
                    helperText={errors.contactPhone?.message}
                    {...register("contactPhone")}
                />
                <Divider>
                    <Typography variant="caption" textTransform="uppercase">
                        {t("projectForm.sectionStatus")}
                    </Typography>
                </Divider>
                <SelectElement
                    control={control}
                    label={t("projectForm.labelProjectStatus")}
                    rules={{ required: true }}
                    name="status"
                    valueKey="value"
                    options={Object.values(GqlProjectStatus).map(value => ({
                        label: t(`projectForm.projectStatusValue.${value}`),
                        value,
                    }))}
                    disabled={!isStatusChangeAllowed}
                    helperText={statusChangeReason}
                />
                <SwitchElement
                    name="isSpeciesManagementPlan"
                    label={t("projectForm.labelIsSpeciesManagementPlan")}
                    control={control}
                />
                <SwitchElement
                    name="showAllObservationsToClientUser"
                    label={t("projectForm.labelShowAllObservationsToClientUser")}
                    control={control}
                />
                <Divider>
                    <Typography variant="caption" textTransform="uppercase">
                        {t("projectForm.sectionFinancials")}
                    </Typography>
                </Divider>
                <TextField
                    label={t("projectForm.labelTravelAllowanceKms")}
                    error={!!errors.travelAllowanceKms}
                    helperText={errors.travelAllowanceKms?.message}
                    type="number"
                    {...register("travelAllowanceKms", {
                        valueAsNumber: true,
                        min: {
                            value: 0,
                            message: t("projectForm.validationMinimum0"),
                        },
                    })}
                />
                <TextField
                    label={t("projectForm.labelTravelAllowancePerKmCents")}
                    error={!!errors.travelAllowancePerKmInCents}
                    helperText={errors.travelAllowancePerKmInCents?.message}
                    type="number"
                    {...register("travelAllowancePerKmInCents", {
                        valueAsNumber: true,
                        min: {
                            value: 0,
                            message: t("projectForm.validationMinimum0"),
                        },
                    })}
                />
                <SelectElement
                    control={control}
                    label={t("projectForm.labelTravelAllowanceTimeInSeconds")}
                    rules={{ required: true }}
                    name="travelAllowanceTimeInSeconds"
                    valueKey="value"
                    options={[
                        {
                            label: t("projectForm.selectTravelAllowanceTimeInSeconds.30-minutes"),
                            value: 30 * 60,
                        },
                        {
                            label: t("projectForm.selectTravelAllowanceTimeInSeconds.45-minutes"),
                            value: 45 * 60,
                        },
                        {
                            label: t("projectForm.selectTravelAllowanceTimeInSeconds.1-hour"),
                            value: 60 * 60,
                        },
                        {
                            label: t("projectForm.selectTravelAllowanceTimeInSeconds.1.5-hours"),
                            value: 90 * 60,
                        },
                        {
                            label: t("projectForm.selectTravelAllowanceTimeInSeconds.2-hours"),
                            value: 120 * 60,
                        },
                        {
                            label: t("projectForm.selectTravelAllowanceTimeInSeconds.2.5-hours"),
                            value: 150 * 60,
                        },
                        {
                            label: t("projectForm.selectTravelAllowanceTimeInSeconds.3-hours"),
                            value: 180 * 60,
                        },
                    ]}
                />
                <Button sx={{ mt: 3 }} type="submit" color="primary" variant="contained" disabled={!isDirty} loading={isLoading}>
                    {formButtonLabel}
                </Button>
            </Stack>
        </form>
    );
}

export type ProjectFormData = {
    name: string;
    startAt?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    status: GqlProjectStatus;
    isSpeciesManagementPlan: boolean;
    showAllObservationsToClientUser: boolean;
    travelAllowanceKms: number;
    travelAllowancePerKmInCents: number;
    travelAllowanceTimeInSeconds: number;
};
